import React from 'react';

import { Box, Button, Heading, Paragraph, Text } from '@zillow/constellation-10';

import { useSelector } from '../../../selectors/useSelector';
import { UserSelectors } from '../../../selectors';

import PropertiesConstants from '../../../constants/PropertiesConstants';
import { ZRMOverview } from '../ZRMOverview';
import { NoSearchResults } from '../NoSearchResults';
import PropertiesViewGrid from '../PropertiesViewGrid';
import PropertiesViewList from '../PropertiesViewList';
import ClaimYourListingDetails from '../../ClaimYourListingDetails';

import { StyledCard, StyledCenterAlignedContainer } from './StyledComponents';

import type { PropertyProps } from '../../../model/Property';

type Props = {
  listings: Array<PropertyProps>;
  showArchived: boolean;
  isOnClaimed: boolean;
  isLoading: boolean;
  didPropertiesLoad: boolean;
  viewType?: string;
  onRemoveSearchTextClick?: (value: string) => void;
  hasAnyArchivedListings: boolean;
  toggleArchivedView: () => void;
};

const PropertiesDisplay = ({
  listings,
  showArchived,
  isOnClaimed,
  isLoading,
  didPropertiesLoad,
  viewType = PropertiesConstants.PROPERTIES_VIEW_TYPE.GRID,
  onRemoveSearchTextClick = () => {},
  hasAnyArchivedListings,
  toggleArchivedView,
}: Props) => {
  const user = useSelector((state) => UserSelectors.makeUser(state));
  const searchText = useSelector((state) => state.properties.propertiesFilters.searchText);

  const renderTimedOutDisplay = () => (
    <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginTop: '1200' }}>
      <Text
        css={{
          textAlign: 'center',
          padding: { base: '0 {spacing.400}', lg: '0' },
          marginBottom: '800',
          marginInline: !user.isEnterpriseCustomer ? '400' : undefined,
        }}
      >
        We are having trouble retrieving your properties at this time. Please refresh the page or check back soon.
      </Text>
    </Box>
  );

  const renderNoListingsFound = () => {
    if (isOnClaimed) {
      return (
        <StyledCenterAlignedContainer>
          <Heading level={2} textStyle="heading-md" fontFamily="heading-alt">
            Claim and list your properties for free
          </Heading>
          <Paragraph css={{ marginBlock: '600' }}>
            Help renters discover your unadvertised properties in just a few steps.
          </Paragraph>
          <Button tone="brand" emphasis="filled" css={{ marginBottom: 'loosest' }} asChild>
            <a href="/rental-manager/ad-management/how-claiming-works">Get started</a>
          </Button>
          <ClaimYourListingDetails />
          <>
            <Paragraph css={{ marginTop: '1000' }}>
              Unclaimed properties with under 25 units may already be listed on Zillow, Trulia and HotPads.
            </Paragraph>
            <Paragraph>Claim yours to manage your portfolio in one place and move faster to fill vacancies.</Paragraph>
          </>
        </StyledCenterAlignedContainer>
      );
    }

    if (searchText) {
      return <NoSearchResults showArchived={showArchived} onRemoveSearchTextClick={onRemoveSearchTextClick} />;
    }

    if (showArchived) {
      return (
        <Text css={{ textAlign: 'center', padding: { base: '0 {spacing.400}', lg: '0' }, marginBottom: '800' }}>
          You don&apos;t have any archived properties.
        </Text>
      );
    }

    if (user.features.canAddListing) {
      return (
        <ZRMOverview hasAnyArchivedListings={hasAnyArchivedListings} handleShowArchivedClick={toggleArchivedView} />
      );
    }

    return (
      <Text css={{ textAlign: 'center', padding: { base: '0 {spacing.400}', lg: '0' }, marginBottom: '800' }}>
        No properties found.
      </Text>
    );
  };

  const renderView = () =>
    viewType === PropertiesConstants.PROPERTIES_VIEW_TYPE.GRID ? (
      <PropertiesViewGrid listings={listings} />
    ) : (
      <PropertiesViewList listings={listings} />
    );

  const renderListingsDisplay = () => (
    <React.Fragment>
      {listings.length > 0 ? (
        renderView()
      ) : (
        <StyledCard appearance="borderless" cardType="secondary" cardElevation={0} paddingY="xl">
          {renderNoListingsFound()}
        </StyledCard>
      )}
    </React.Fragment>
  );

  return (
    <div data-testid="PropertiesDisplay">
      {!didPropertiesLoad && !isLoading ? renderTimedOutDisplay() : renderListingsDisplay()}
    </div>
  );
};

export { PropertiesDisplay };
export default PropertiesDisplay;
